import { config } from '@ps/config';

// ─── Types ────────────────────────────────────────────────────────────────

type ImageFit = 'contain' | 'cover' | 'fill' | 'inside' | 'outside';

export interface ImageResizeOptions {
  width: number;
  height: number;
  fit?: ImageFit;
  quality?: number;
}

interface NextImageLoaderParams {
  src: string;
  width: number;
  quality?: number;
}

// ─── Resize URL builder ───────────────────────────────────────────────────

/**
 * Builds a resize-service URL for the given source image.
 * Returns the original URL for non-http, GIF, or already-resized URLs.
 */
export function buildResizeUrl(
  src: string,
  options: ImageResizeOptions,
): string {
  if (!src.startsWith('http')) return src;
  if (src.includes('.gif')) return src;
  if (src.includes(config.imageResizeEndpoint)) return src;

  const params = new URLSearchParams({
    url: src,
    width: String(Math.round(options.width)),
    height: String(Math.round(options.height)),
    fit: options.fit ?? 'cover',
  });

  if (options.quality) {
    params.set('quality', String(options.quality));
  }

  return `${config.imageResizeEndpoint}?${params.toString()}`;
}

// ─── Next.js Image loader factory ─────────────────────────────────────────

/**
 * Creates a Next.js `<Image loader={...} />` function with preset resize options.
 *
 * @example
 * ```tsx
 * const thumbnailLoader = createImageLoader({ width: 200, height: 200, fit: 'cover' });
 * <Image loader={thumbnailLoader} src={url} width={200} height={200} />
 * ```
 */
export function createImageLoader(options: ImageResizeOptions) {
  return function loader({ src, quality }: NextImageLoaderParams): string {
    return buildResizeUrl(src, {
      ...options,
      quality: quality ?? options.quality,
    });
  };
}

/**
 * Default Next.js Image loader — uses the `width` provided by Next.js
 * as both width and height (square crop, cover fit).
 */
export function imageLoader({
  src,
  width,
  quality,
}: NextImageLoaderParams): string {
  return buildResizeUrl(src, { width, height: width, fit: 'cover', quality });
}
